import { FC, ReactNode, useState } from 'react'
import moment from 'moment'
import { ReactComponent as CommentIcon } from 'src/assets/svg/comment.svg'
import { ReactComponent as ExternalLinkIcon } from 'src/assets/svg/external-link.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkedInIcon } from 'src/assets/svg/linkedin.svg'
import { ReactComponent as LikeHeartIcon } from 'src/assets/svg/like-heart.svg'
import { ReactComponent as RepostsIcon } from 'src/assets/svg/reposts.svg'
import { ReactComponent as SharesIcon } from 'src/assets/svg/shares.svg'
import { ReactComponent as TiktokIcon } from 'src/assets/svg/tiktok.svg'
import { ReactComponent as YouTubeIcon } from 'src/assets/svg/youtube.svg'
import { numberWithCommas } from 'src/utils/numberFormat'
import { IndividualPost } from 'src/services/__generated__/api'
import styles from './postCard.module.scss'

const PLATFORM_ICONS: Record<string, ReactNode> = {
  facebook: <FacebookIcon />,
  instagram: <InstagramIcon />,
  linkedin: <LinkedInIcon />,
  tiktok: <TiktokIcon />,
  youtube: <YouTubeIcon />,
}

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  twitter: 'X (Twitter)',
  youtube: 'YouTube',
}

const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

interface PostCardProps {
  // Without it the card has no View Details link (the topic popup, where a
  // second modal on top of the first is not designed yet).
  onViewDetails?: () => void
  post: IndividualPost
  showImage?: boolean
  variant?: 'horizontal' | 'vertical'
}

export const PostCard: FC<PostCardProps> = ({
  onViewDetails,
  post,
  showImage = true,
  variant = 'horizontal',
}) => {
  const [imgError, setImgError] = useState(false)
  const platform = post.platform || ''
  const breakdown = post.commentsBreakdown
  const postUrl = (post as IndividualPost & { postUrl?: string }).postUrl

  return (
    <div className={`${styles.postCard} ${styles[variant]}`}>
      <div className={styles.cardHeader}>
        <span className={styles.platform}>
          <span className={styles.platformIcon}>
            {PLATFORM_ICONS[platform]}
          </span>
          <span className={styles.platformName}>
            {PLATFORM_LABELS[platform] || platform}
          </span>
          {postUrl ? (
            <a
              href={postUrl}
              onClick={(e) => e.stopPropagation()}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ExternalLinkIcon className={styles.externalLinkIcon} />
            </a>
          ) : (
            <ExternalLinkIcon className={styles.externalLinkIcon} />
          )}
        </span>
        {post.duplicatesCount && post.duplicatesCount > 0 && (
          <span className={styles.duplicatesBadge}>
            <span className={styles.warningIcon}>▲</span>
            {post.duplicatesCount} Duplicates Found
          </span>
        )}
      </div>

      <div className={styles.contentRow}>
        {showImage && post.imageUrl && !imgError && (
          <div className={styles.imageWrapper}>
            <img
              alt="Post thumbnail"
              className={styles.thumbnail}
              onError={() => setImgError(true)}
              src={post.imageUrl}
            />
          </div>
        )}
        <div className={styles.textContent}>
          <p className={styles.content}>{post.content}</p>
          {post.date && moment(post.date).isValid() && (
            <span className={styles.date}>
              {moment(post.date).format('YYYY-MM-DD HH:mm')}
            </span>
          )}
        </div>
      </div>

      <div className={styles.metricsRow}>
        <div className={styles.metrics}>
          <span className={styles.metricItem}>
            <LikeHeartIcon className={styles.metricIcon} />
            {numberWithCommas(post.reactions || 0)}
          </span>
          <span className={styles.metricItem}>
            <CommentIcon className={styles.metricIcon} />
            {numberWithCommas(post.commentsCount || 0)}
            {/* Only when the read carries the split: a row of zeros would claim
                every comment is unclassified. */}
            {breakdown && (
              <span className={styles.breakdown}>
                (
                <span
                  className={styles.dot}
                  style={{ backgroundColor: SENTIMENT_COLORS.positive }}
                />
                {breakdown.positive || 0}
                <span
                  className={styles.dot}
                  style={{ backgroundColor: SENTIMENT_COLORS.neutral }}
                />
                {breakdown.neutral || 0}
                <span
                  className={styles.dot}
                  style={{ backgroundColor: SENTIMENT_COLORS.negative }}
                />
                {breakdown.negative || 0})
              </span>
            )}
          </span>
          <span className={styles.metricItem} title="Shares">
            <SharesIcon className={styles.metricIcon} />
            {numberWithCommas(post.shares || 0)}
          </span>
          {/* Instagram reposts: only when reported. Other platforms, and posts
              synced before the field existed, carry none -- a 0 would claim a
              count nobody read. */}
          {typeof post.reposts === 'number' && (
            <span className={styles.metricItem} title="Reposts">
              <RepostsIcon className={styles.metricIcon} />
              {numberWithCommas(post.reposts)}
            </span>
          )}
        </div>

        {onViewDetails && (
          <button
            className={styles.viewDetails}
            onClick={onViewDetails}
            type="button"
          >
            View Details
            <span className={styles.chevron}>›</span>
          </button>
        )}
      </div>
    </div>
  )
}
